/**
 * How a tab strip that does not fit is moved sideways, decided away from the
 * markup.
 *
 * The owner, 2026-11-02: the settings strip "has no way to navigate on smaller
 * width screens", so add arrows left and right, and moving the mouse towards
 * the edge of the strip should scroll it. The strip hides its scrollbar on
 * purpose (it is a row of words, not a pane), and a mouse has no sideways
 * wheel, so before this a tab past the edge could only be reached with a
 * keyboard or a finger.
 *
 * What lives here, all of it plain arithmetic on the three numbers a scroll
 * container reports (`scrollLeft`, `scrollWidth`, `clientWidth`):
 *
 * - which arrows show, with a one pixel tolerance, because a zoomed page
 *   reports fractional widths and an arrow that flickers on for half a pixel of
 *   hidden content is worse than none
 * - how far an arrow press moves the strip
 * - how fast a mouse resting near an edge scrolls it, faster the closer it is
 * - what a vertical wheel turn does over a strip that can move sideways
 * - the `scrollLeft` that brings the open tab into view with room to spare
 *
 * The strip is laid out left to right, as every screen in this app is, so
 * `scrollLeft` runs from 0 at the start to `scrollWidth - clientWidth`.
 *
 * Nothing here imports React, so it is covered by a plain unit test.
 */

/** Less hidden content than this is treated as none, so no arrow shows for it. */
export const EDGE_TOLERANCE_PX = 1

/**
 * The width of each edge's overlay (the solid backing behind the arrow, the
 * 28px arrow and the fade beside it) and of the zone where a resting mouse
 * scrolls the strip. One number for both, so the fade a person sees is exactly
 * the region that moves when they point at it.
 */
export const EDGE_ZONE_PX = 56

/** Hover scrolling at the inner boundary of the zone, in pixels a second. */
export const HOVER_MIN_SPEED_PX_S = 80

/** Hover scrolling with the pointer on the strip's outer edge, in pixels a second. */
export const HOVER_MAX_SPEED_PX_S = 720

/**
 * One frame never advances the strip by more than this much time, so a tab
 * that was in the background, or a frame that stalled, does not jump the strip
 * by seconds' worth of scrolling when it resumes.
 */
export const HOVER_MAX_FRAME_MS = 50

/** An arrow press moves the strip by this share of what is visible. */
export const STEP_FRACTION = 0.8

/** And by at least this much, so a very narrow strip still moves a whole tab. */
export const STEP_MIN_PX = 120

/**
 * After an arrow press the hover scroll waits this long, so the smooth scroll
 * the press started can finish instead of being cut short by the next frame.
 */
export const STEP_SETTLE_MS = 450

/**
 * Room left between the open tab and the edge it is scrolled to, so it does
 * not land under an arrow or the fade.
 */
export const REVEAL_MARGIN_PX = EDGE_ZONE_PX

/** A wheel "line" in pixels, for the browsers that report the wheel in lines. */
export const WHEEL_LINE_PX = 16

export interface StripMetrics {
  scrollLeft: number
  scrollWidth: number
  clientWidth: number
}

export interface StripEdges {
  /** There is hidden content to the left, so the left arrow shows. */
  left: boolean
  /** There is hidden content to the right, so the right arrow shows. */
  right: boolean
}

export const NO_EDGES: StripEdges = { left: false, right: false }

function clamp(value: number, min: number, max: number): number {
  return Math.min(Math.max(value, min), max)
}

/** The furthest the strip can scroll, never negative. */
export function maxScrollLeft(metrics: Pick<StripMetrics, 'scrollWidth' | 'clientWidth'>): number {
  return Math.max(0, metrics.scrollWidth - metrics.clientWidth)
}

/** Which arrows show. None at all when everything fits. */
export function stripEdges(metrics: StripMetrics): StripEdges {
  const max = maxScrollLeft(metrics)
  if (max <= EDGE_TOLERANCE_PX) return NO_EDGES
  return {
    left: metrics.scrollLeft > EDGE_TOLERANCE_PX,
    right: metrics.scrollLeft < max - EDGE_TOLERANCE_PX,
  }
}

export function sameEdges(a: StripEdges, b: StripEdges): boolean {
  return a.left === b.left && a.right === b.right
}

/** How far one arrow press moves the strip: most of what is visible. */
export function stepDistance(clientWidth: number): number {
  return Math.max(STEP_MIN_PX, Math.round(clientWidth * STEP_FRACTION))
}

/** Where one arrow press lands, kept inside the strip. -1 is left, 1 is right. */
export function stepTarget(metrics: StripMetrics, direction: -1 | 1): number {
  return clamp(metrics.scrollLeft + direction * stepDistance(metrics.clientWidth), 0, maxScrollLeft(metrics))
}

/**
 * The hover speed for a pointer this far inside an edge, in pixels a second.
 *
 * Zero outside the zone. Inside it the speed eases in, slow at the zone's inner
 * boundary so a person reaching for a tab near the edge is not pulled past it,
 * and fastest with the pointer on the edge itself.
 */
export function hoverSpeed(distanceFromEdge: number, zone: number = EDGE_ZONE_PX): number {
  if (zone <= 0 || distanceFromEdge >= zone) return 0
  const closeness = 1 - clamp(distanceFromEdge, 0, zone) / zone
  return HOVER_MIN_SPEED_PX_S + (HOVER_MAX_SPEED_PX_S - HOVER_MIN_SPEED_PX_S) * closeness * closeness
}

/**
 * The hover velocity for a pointer at `pointerX` across a strip `width` wide,
 * in pixels a second: negative scrolls left, positive right, zero stays put.
 *
 * Only an edge with more content beyond it scrolls, so a pointer resting by
 * the first tab of a strip that is already at its start does nothing. On a
 * strip too narrow for two zones the nearer edge wins.
 */
export function hoverVelocity(pointerX: number, width: number, edges: StripEdges, zone: number = EDGE_ZONE_PX): number {
  if (width <= 0 || pointerX < 0 || pointerX > width) return 0
  const fromLeft = pointerX
  const fromRight = width - pointerX
  if (fromLeft <= fromRight) {
    const speed = edges.left ? hoverSpeed(fromLeft, zone) : 0
    // Never -0, which a caller comparing with 0 by identity would read as moving.
    return speed === 0 ? 0 : -speed
  }
  return edges.right ? hoverSpeed(fromRight, zone) : 0
}

/**
 * The whole pixels to scroll this frame, carrying the fraction to the next.
 *
 * Browsers round `scrollLeft` to whole (device) pixels, so adding the 1.3px a
 * slow frame is owed would scroll nothing, frame after frame, and the strip
 * would sit still at the slow end of the zone. Carrying the remainder makes
 * every speed in the zone move.
 */
export function hoverStep(velocity: number, frameMs: number, carry: number): { step: number; carry: number } {
  const owed = carry + (velocity * clamp(frameMs, 0, HOVER_MAX_FRAME_MS)) / 1000
  // `|| 0` folds the -0 that truncating a small negative leaves into a plain 0.
  const step = Math.trunc(owed) || 0
  return { step, carry: owed - step }
}

export interface WheelInput {
  deltaX: number
  deltaY: number
  /** 0 pixels, 1 lines, 2 pages, as `WheelEvent.deltaMode`. */
  deltaMode: number
  /** A pinch on a trackpad arrives as a wheel with Control held, and is the browser's zoom. */
  ctrlKey?: boolean
}

/**
 * What a wheel turn over the strip does to it: the pixels to add to
 * `scrollLeft`, or 0 to leave the event to the page.
 *
 * A vertical turn over a strip that can move that way moves it sideways, down
 * to the right and up to the left. A turn the strip cannot follow (it already
 * fits, or it is at that end) is 0, so the page scrolls as it always did and a
 * strip at its end never traps the wheel. A mostly sideways gesture (a trackpad
 * swipe, a tilt wheel) is 0 because the browser already scrolls the strip for
 * it.
 */
export function wheelScrollDelta(wheel: WheelInput, metrics: StripMetrics): number {
  if (wheel.ctrlKey) return 0
  if (Math.abs(wheel.deltaX) >= Math.abs(wheel.deltaY)) return 0
  const unit = wheel.deltaMode === 1 ? WHEEL_LINE_PX : wheel.deltaMode === 2 ? metrics.clientWidth : 1
  const wanted = wheel.deltaY * unit
  const edges = stripEdges(metrics)
  if (wanted > 0 && !edges.right) return 0
  if (wanted < 0 && !edges.left) return 0
  return clamp(metrics.scrollLeft + wanted, 0, maxScrollLeft(metrics)) - metrics.scrollLeft
}

export interface RevealInput extends StripMetrics {
  /** The tab's left edge, measured from the start of the strip's content. */
  itemStart: number
  /** The tab's right edge, on the same measure. */
  itemEnd: number
  margin?: number
}

/**
 * The `scrollLeft` that shows the open tab with `margin` to spare on both
 * sides, or the current one when it is already in view that way.
 *
 * It moves as little as it can: a tab off the right edge is brought in just
 * far enough to clear the right arrow, not centred. A tab wider than the room
 * left between the margins is aligned to its start, so its label's first words
 * are what shows.
 */
export function revealScrollLeft({ scrollLeft, scrollWidth, clientWidth, itemStart, itemEnd, margin = REVEAL_MARGIN_PX }: RevealInput): number {
  const max = maxScrollLeft({ scrollWidth, clientWidth })
  if (max <= EDGE_TOLERANCE_PX) return scrollLeft
  // A strip too narrow for both margins keeps what room it has for the tab.
  const room = Math.max(0, Math.min(margin, (clientWidth - (itemEnd - itemStart)) / 2))
  const visibleStart = scrollLeft + room
  const visibleEnd = scrollLeft + clientWidth - room
  let target = scrollLeft
  if (itemStart < visibleStart) target = itemStart - room
  else if (itemEnd > visibleEnd) target = Math.min(itemEnd + room - clientWidth, itemStart - room)
  return clamp(Math.round(target), 0, max)
}
